/**
 * Magazyn Bukietowej — /blog
 * Editorial magazine-style blog listing.
 */
import { Link } from 'wouter';
import { ArrowRight } from 'lucide-react';
import { useInView } from '@/lib/useInView';
import {
  POSTS, formatDate,
  type Post,
} from '@/data/blog';
import { Footer } from '@/components/sections/Footer';
import { useSeo } from '@/lib/useSeo';
import { PAGE_SEO } from '@/data/pageSeoConfig';
import { articleUrl } from '@/lib/articleUrl';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';

/* ── Tokens ─────────────────────────────────────────────── */
const GOLD   = '#e05590';
const DARK   = '#2a1f14';
const MUTED  = 'rgba(42,31,20,0.50)';
const LINE   = 'rgba(210,60,120,0.18)';
const CREAM  = '#fff';
const CREAM2 = '#fff';
const CREAM3 = '#fff';


/* ── Shared ─────────────────────────────────────────────── */
function CategoryBadge({ cat }: { cat: string }) {
  return (
    <span style={{
      fontFamily: 'DM Sans, sans-serif', fontSize: 9, letterSpacing: '0.28em',
      textTransform: 'uppercase', color: GOLD, background: `${GOLD}12`,
      padding: '3px 9px', display: 'inline-block',
    }}>
      {cat}
    </span>
  );
}

function ReadMore({ slug }: { slug: string }) {
  return (
    <Link href={articleUrl(slug)} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontFamily: 'DM Sans, sans-serif', fontSize: 10.5, letterSpacing: '0.2em',
      textTransform: 'uppercase', color: DARK, textDecoration: 'none',
      borderBottom: `1px solid ${GOLD}55`, paddingBottom: 2,
    }}>
      Czytaj <ArrowRight size={11} />
    </Link>
  );
}

function PostImage({ src, alt, style }: { src?: string; alt?: string; style?: React.CSSProperties }) {
  if (!src) {
    return (
      <div style={{ background: '#fff', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', ...style }}>
        <span style={{ fontFamily: 'DM Sans', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: `${GOLD}55` }}>Bukietowa</span>
      </div>
    );
  }
  return <img src={src} alt={alt ?? ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', ...style }} />;
}

/* ── POST CARD ──────────────────────────────────────────── */
function PostCard({ post, delay = 0 }: { post: Post; delay?: number }) {
  const { ref, inView } = useInView<HTMLElement>();
  return (
    <article ref={ref} style={{ height: '100%', opacity: inView ? undefined : 0, animationDelay: `${delay}s` }} className={inView ? 'bk-fade-up' : undefined}>
      <Link href={articleUrl(post.slug)} style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div
          style={{
            background: '#fff', border: `1px solid ${LINE}`,
            height: '100%', display: 'flex', flexDirection: 'column',
            transition: 'box-shadow 0.2s, border-color 0.2s',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 24px rgba(42,31,20,0.09)'; (e.currentTarget as HTMLDivElement).style.borderColor = `${GOLD}55`; }}
          onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'; (e.currentTarget as HTMLDivElement).style.borderColor = LINE; }}
        >
          {/* Image */}
          <div style={{ aspectRatio: '3/2', overflow: 'hidden', background: '#fff', flexShrink: 0 }}>
            <PostImage src={post.image} alt={post.imageAlt} />
          </div>
          {/* Body */}
          <div style={{ padding: 'clamp(16px,2vw,22px)', flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginBottom: 8 }}>
              <CategoryBadge cat={post.category} />
            </div>
            <h3 style={{
              fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontSize: 'clamp(17px,1.6vw,20px)', fontWeight: 400,
              color: DARK, margin: '0 0 10px', lineHeight: 1.25,
            }}>
              {post.title}
            </h3>
            <p style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: 13,
              color: MUTED, lineHeight: 1.7, margin: '0 0 16px', flex: 1,
            }}>
              {post.lead}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: 12, borderTop: `1px solid ${LINE}` }}>
              <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: `${DARK}44` }}>
                {formatDate(post.publishedAt)} · {post.readTime} min
              </span>
              <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: GOLD }}>
                Czytaj →
              </span>
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
}

/* ── 1. FEATURED HERO ───────────────────────────────────── */
function FeaturedHero({ post }: { post: Post }) {
  return (
    <Link href={articleUrl(post.slug)} style={{ textDecoration: 'none', display: 'block' }}>
      <article className="bk-fade-in" style={{ width: '100%', background: '#fff', overflow: 'hidden', cursor: 'pointer' }}>
        {/* Image */}
        <div style={{ aspectRatio: '21/9', minHeight: 240, overflow: 'hidden' }}>
          <PostImage src={post.image} alt={post.imageAlt} style={{ objectPosition: 'center 30%' }} />
        </div>
        {/* Text */}
        <div style={{ padding: 'clamp(24px,4vw,52px)', maxWidth: 680 }}>
          <CategoryBadge cat={post.category} />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.8rem,3.5vw,3rem)', fontWeight: 400, color: DARK, margin: '10px 0 12px', lineHeight: 1.1 }}>
            {post.title}
          </h2>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: MUTED, lineHeight: 1.7, margin: '0 0 20px' }}>
            {post.lead}
          </p>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontFamily: 'DM Sans, sans-serif', fontSize: 10.5, letterSpacing: '0.2em', textTransform: 'uppercase',
            color: GOLD, border: `1px solid ${GOLD}55`, padding: '10px 22px',
          }}>
            Czytaj artykuł <ArrowRight size={12} />
          </span>
        </div>
        {/* Read time badge */}
        <div style={{ padding: '0 clamp(24px,4vw,52px) 24px' }}>
          <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, color: MUTED, letterSpacing: '0.1em' }}>
            {post.readTime} min
          </span>
        </div>
      </article>
    </Link>
  );
}

/* ── 2. EDITORIAL ASYMMETRIC GRID ───────────────────────── */
function EditorialGrid({ posts }: { posts: Post[] }) {
  if (posts.length === 0) return null;
  const [main, ...rest] = posts;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12,1fr)', gap: 'clamp(8px,1.2vw,16px)' }}>
      {/* Main large card — 7 cols */}
      {main && (
        <article style={{ gridColumn: 'span 12' } as React.CSSProperties}
          className="bk-fade-up md:col-span-7"
        >
          <Link href={articleUrl(main.slug)} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
            <div style={{ overflow: 'hidden', background: '#fff' }}>
              <div style={{ aspectRatio: '4/3', overflow: 'hidden' }}>
                <PostImage src={main.image} alt={main.imageAlt} />
              </div>
              <div style={{ padding: 24 }}>
                <CategoryBadge cat={main.category} />
                <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.4rem,2.5vw,2rem)', fontWeight: 400, color: DARK, margin: '8px 0 0', lineHeight: 1.15 }}>
                  {main.title}
                </h3>
              </div>
            </div>
          </Link>
        </article>
      )}

      {/* Smaller cards — 5 cols, stacked */}
      <div style={{ gridColumn: 'span 12', display: 'flex', flexDirection: 'column', gap: 'clamp(8px,1.2vw,16px)' }}
        className="md:col-span-5">
        {rest.slice(0, 2).map((post, i) => (
          <article key={post.slug} style={{ animationDelay: `${0.08 + i * 0.06}s` }} className="bk-fade-up">
            <Link href={articleUrl(post.slug)} style={{ textDecoration: 'none', display: 'grid', gridTemplateColumns: '120px 1fr', gap: 16, alignItems: 'start', background: CREAM2, padding: 16 }}>
              <div style={{ aspectRatio: '1/1', overflow: 'hidden', background: '#fff' }}>
                <PostImage src={post.image} alt={post.imageAlt} />
              </div>
              <div>
                <CategoryBadge cat={post.category} />
                <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(15px,1.6vw,18px)', fontWeight: 400, color: DARK, margin: '8px 0 8px', lineHeight: 1.25 }}>
                  {post.title}
                </h3>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: MUTED, margin: 0 }}>
                  {formatDate(post.publishedAt)}
                </p>
              </div>
            </Link>
          </article>
        ))}
      </div>

      {/* Row 2 — up to 3 cards across 12 cols */}
      {rest.slice(2, 5).map((post, i) => (
        <article key={post.slug} style={{ gridColumn: 'span 4', animationDelay: `${0.1 + i * 0.06}s` }} className="bk-fade-up">
          <Link href={articleUrl(post.slug)} style={{ textDecoration: 'none', display: 'block' }}>
            <div style={{ aspectRatio: '3/2', overflow: 'hidden', background: '#fff', marginBottom: 12 }}>
              <PostImage src={post.image} alt={post.imageAlt} />
            </div>
            <CategoryBadge cat={post.category} />
            <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(15px,1.5vw,19px)', fontWeight: 400, color: DARK, margin: '8px 0 0', lineHeight: 1.25 }}>
              {post.title}
            </h3>
          </Link>
        </article>
      ))}
    </div>
  );
}

/* ── 3. KWIATY — magazine chapter ───────────────────────── */
function FlowerChapterSection({ posts }: { posts: Post[] }) {
  const { ref, inView } = useInView<HTMLElement>();
  return (
    <section ref={ref} style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <div style={{ opacity: inView ? undefined : 0, marginBottom: 'clamp(32px,4vw,48px)' }} className={inView ? 'bk-fade-up' : undefined}>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9.5, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD, margin: '0 0 10px' }}>
            Rozdział
          </p>
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2.4rem,4.5vw,4rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.04, letterSpacing: '-0.01em' }}>
            Kwiaty, o których<br />
            <em style={{ color: GOLD, fontStyle: 'italic' }}>warto wiedzieć</em>
          </h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(100%,240px),1fr))', gap: 'clamp(12px,2vw,20px)' }}>
          {posts.map((post, i) => (
            <article key={post.slug} style={{ opacity: inView ? undefined : 0, animationDelay: `${i * 0.08}s` }} className={inView ? 'bk-fade-up' : undefined}>
              <Link href={articleUrl(post.slug)} style={{ textDecoration: 'none', display: 'block' }}>
                <div style={{ aspectRatio: '3/4', overflow: 'hidden', background: '#fff', marginBottom: 14 }}>
                  <PostImage src={post.image} alt={post.imageAlt} />
                </div>
                <CategoryBadge cat={post.category} />
                <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(16px,1.6vw,20px)', fontWeight: 400, color: DARK, margin: '8px 0 10px', lineHeight: 1.25 }}>
                  {post.title}
                </h3>
                <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10.5, letterSpacing: '0.2em', textTransform: 'uppercase', color: DARK, borderBottom: `1px solid ${GOLD}55`, paddingBottom: 2 }}>
                  Czytaj →
                </span>
              </Link>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── 4. PORADNIK — numbered editorial list ──────────────── */
function GuideSection({ posts }: { posts: Post[] }) {
  const { ref, inView } = useInView<HTMLElement>();
  return (
    <section ref={ref} style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,380px),1fr))', gap: 'clamp(40px,6vw,80px)', alignItems: 'start' }}>
          {/* Left — heading */}
          <div style={{ opacity: inView ? undefined : 0 }} className={inView ? 'bk-fade-up' : undefined}>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9.5, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD, margin: '0 0 14px' }}>
              Praktycznie
            </p>
            <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem,3.5vw,3.2rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.1 }}>
              Poradnik<br />Bukietowej
            </h2>

          </div>

          {/* Right — numbered list */}
          <div style={{ opacity: inView ? undefined : 0, animationDelay: '0.1s' }} className={inView ? 'bk-fade-up' : undefined}>
            {posts.map((post, i) => (
              <Link key={post.slug} href={articleUrl(post.slug)} style={{ textDecoration: 'none', display: 'flex', gap: 20, alignItems: 'flex-start', padding: 'clamp(18px,2.5vw,26px) 0', borderBottom: `1px solid ${LINE}` }}>
                <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(24px,3vw,32px)', fontWeight: 400, color: `${GOLD}44`, lineHeight: 1, flexShrink: 0, minWidth: 32 }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div style={{ flex: 1 }}>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9.5, letterSpacing: '0.28em', textTransform: 'uppercase', color: GOLD, margin: '0 0 6px' }}>
                    {post.category}
                  </p>
                  <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(17px,1.8vw,21px)', fontWeight: 400, color: DARK, margin: '0 0 6px', lineHeight: 1.25 }}>
                    {post.title}
                  </h3>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: MUTED, margin: 0, lineHeight: 1.65 }}>
                    {post.lead}
                  </p>
                </div>
                <ArrowRight size={14} color={`${GOLD}77`} style={{ flexShrink: 0, marginTop: 4 }} />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── 5. ZZA KULIS ───────────────────────────────────────── */
function BehindScenesSection({ posts }: { posts: Post[] }) {
  const main = posts[0];
  const { ref, inView } = useInView<HTMLElement>();
  if (!main) return null;

  return (
    <section ref={ref} style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <div style={{ opacity: inView ? undefined : 0, marginBottom: 'clamp(32px,4vw,48px)' }} className={inView ? 'bk-fade-up' : undefined}>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9.5, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD, margin: '0 0 12px' }}>
            Za kulisami
          </p>
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem,3.5vw,3.2rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.1 }}>
            Zza kulis Bukietowej
          </h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,480px),1fr))', gap: 'clamp(24px,4vw,48px)', alignItems: 'center' }}>
          <div style={{ opacity: inView ? undefined : 0 }} className={inView ? 'bk-fade-up' : undefined}>
            <div style={{ aspectRatio: '4/3', overflow: 'hidden', background: '#fff' }}>
              <PostImage src={main.image} alt={main.imageAlt} />
            </div>
          </div>
          <div style={{ opacity: inView ? undefined : 0, animationDelay: '0.1s' }} className={inView ? 'bk-fade-up' : undefined}>
            <CategoryBadge cat={main.category} />
            <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.8rem,3vw,2.8rem)', fontWeight: 400, color: DARK, margin: '12px 0 16px', lineHeight: 1.1 }}>
              {main.title}
            </h3>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14.5, color: MUTED, lineHeight: 1.85, margin: '0 0 24px' }}>
              {main.lead}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <ReadMore slug={main.slug} />
              <span style={{ fontFamily: 'DM Sans', fontSize: 12, color: MUTED }}>{formatDate(main.publishedAt)}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── PAGE ────────────────────────────────────────────────── */
export default function BlogPage() {
  usePrerenderReady();
  useSeo(PAGE_SEO.blog);
  const visible = POSTS;

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <PageBreadcrumb crumbs={[{ label: 'Blog' }]} />

      {/* ── Masthead ─────────────────────────────────── */}
      <section style={{ background: '#fff', borderBottom: `1px solid ${LINE}`, padding: 'clamp(48px,6vw,72px) 0 clamp(28px,3vw,40px)' }}>
        <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)', textAlign: 'center' }}>
          <div className="bk-fade-up bk-d1">
            <h1 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(3rem,7vw,6rem)', fontWeight: 400, color: DARK, margin: '0 0 16px', lineHeight: 1.0 }}>
              Blog Kwiaciarni Bukietowa.pl
            </h1>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(13px,1.3vw,14.5px)', color: MUTED, margin: '0 0 28px', lineHeight: 1.75, maxWidth: 560, marginLeft: 'auto', marginRight: 'auto' }}>
              Kwiaty, inspiracje, florystyka i wszystko, co warto wiedzieć o pięknie, które tworzymy każdego dnia.
            </p>
            {/* Topics strip */}
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: `${DARK}44`, margin: '0 0 28px' }}>
              Blog kwiaciarni Bukietowa
            </p>

          </div>
        </div>
      </section>

      {/* ── Content ──────────────────────────────────── */}
      <section style={{ padding: 'clamp(40px,5vw,64px) 0 clamp(64px,8vw,120px)' }}>
        <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))',
            gap: 'clamp(16px,2vw,24px)',
            alignItems: 'stretch',
          }}>
            {visible.map((post, i) => (
              <PostCard key={post.slug} post={post} delay={Math.min(i * 0.04, 0.32)} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
